import { DEFAULT_FILTERS } from "components/FilterDrawer/constants";
import { FilterState, GenericOption } from "components/FilterDrawer/types";
import moment from "moment-timezone";
import { TIMEZONE_MAP } from "utils/common-util";

export interface OvertimeSeries {
  name: string;
  data: number[];
  rawData?: number[];
  yAxisIndex?: number;
}
export interface OvertimeCard {
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down';
  tooltip?: string;
  arrowOnly?: boolean;
}

export interface TransformedRunOvertimeData {
  xAxisData: string[];
  series: OvertimeSeries[];
}
export interface BackendBucketData {
  bucket: string;
  total: string;
  success: string;
  failure: string;
  aborted: string;
  inProgress: string;
  delayed: string;
  successPct: string;
  failurePct: string;
  abortedPct: string;
  inProgressPct: string;
  delayedPct: string;
}
export interface BackendRunTrendAvgData {
  totalRuns: number;
  peakDay: string;
  peakCount: number;
  avgRuns: number;
}

/**
 * Parses values with K, M, B suffixes
 * Examples: "1.6K" -> 1600, "2.5M" -> 2500000, "1B" -> 1000000000
 */
export const parseValue = (value: string): number => {
  if (!value || typeof value !== 'string') return 0;

  const trimmed = value.trim().toUpperCase();
  const regex = /^([\d.]+)([KMB])$/;
  const numberMatch = regex.exec(trimmed);

  if (numberMatch) {
    const num = Number.parseFloat(numberMatch[1]);
    const suffix = numberMatch[2];

    switch (suffix) {
      case 'K':
        return Math.round(num * 1_000);
      case 'M':
        return Math.round(num * 1_000_000);
      case 'B':
        return Math.round(num * 1_000_000_000);
      default:
        return 0;
    }
  }

  return Math.round(Number.parseFloat(trimmed)) || 0;
};

/**
 * Formats large numbers with K, M, B suffixes
 * Examples: 1600 -> "1.6K", 2500000 -> "2.5M"
 */
export const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1) + 'K';
  }
  return num.toString();
};

export const formatValueFromPct = (total: number, pct: number): string => {
  const value = (total * pct) / 100;
  return formatNumber(value);
};

export const transformRunOvertimeTrendData = (
  rawData: BackendBucketData[]
): TransformedRunOvertimeData => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return {
      xAxisData: [],
      series: [],
    };
  }

  // Extract x-axis labels (time buckets)
  const xAxisData = rawData.map((item) => item.bucket);
  // Initialize series containers
  const seriesMap: Record<string, number[]> = {
    Total: [],
    Successful: [],
    Failed: [],
    Aborted: [],
    'In Progress': [],
    Delayed: [],
  };

  let totalRunsSum = 0;
  let peakHour = { bucket: '', total: 0 };

  // Process each bucket
  rawData.forEach((bucket) => {
    try {
      const totalValue = parseValue(bucket.total);

      seriesMap.Total.push(totalValue);
      seriesMap.Successful.push(Number.parseFloat(bucket.successPct));
      seriesMap.Failed.push(Number.parseFloat(bucket.failurePct));
      seriesMap.Aborted.push(Number.parseFloat(bucket.abortedPct));
      seriesMap['In Progress'].push(Number.parseFloat(bucket.inProgressPct));
      seriesMap.Delayed.push(Number.parseFloat(bucket.delayedPct));

      totalRunsSum += totalValue;
      if (totalValue > peakHour.total) {
        peakHour = { bucket: bucket.bucket, total: totalValue };
      }
    } catch (error) {
      console.error(`Error processing bucket ${bucket.bucket}:`, error);
    }
  });

  // Build series array
  const series: OvertimeSeries[] = [
    {
      name: 'Total',
      yAxisIndex: 1,
      data: seriesMap.Total,
      rawData: seriesMap.Total,
    },
    {
      name: 'Successful',
      data: seriesMap.Successful,
      rawData: rawData.map(b => parseValue(b.success)),
    },
    {
      name: 'Failed',
      data: seriesMap.Failed,
      rawData: rawData.map(b => parseValue(b.failure)),
    },
    {
      name: 'Aborted',
      data: seriesMap.Aborted,
      rawData: rawData.map(b => parseValue(b.aborted)),
    },
    {
      name: 'In Progress',
      data: seriesMap['In Progress'],
      rawData: rawData.map(b => parseValue(b.inProgress)),
    },
    {
      name: 'Delayed',
      data: seriesMap.Delayed,
      rawData: rawData.map(b => parseValue(b.delayed)),
    },
  ];

  return { xAxisData, series };
};

export const transformRunTrendAvgToCards = (
  rawData: BackendRunTrendAvgData
): OvertimeCard[] => {
  // Validate input
  if (!rawData || typeof rawData !== 'object') {
    return [];
  }

  try {
    const { totalRuns = 0, peakDay = '', peakCount = 0, avgRuns = 0 } = rawData;
    const avg = Number(avgRuns);

    const formatDate = (dateStr: string): string => {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return [
      {
        label: 'Total Run Status',
        value: formatNumber(totalRuns),
        subtitle: ``,
        tooltip: `Total number of job executions recorded over the past 7 days`,
      },
      {
        label: 'Peak Day',
        value: formatDate(peakDay),
        subtitle: ``,
        tooltip: `The day with the highest number of job executions in the past 7 days`,
      },
      {
        label: 'Avg/Day',
        value: avg.toLocaleString(),
        subtitle: ``,
        tooltip: `Average number of job executions per day over the past 7 days`
      },
    ];
  } catch (error) {
    console.error('Error transforming run trend avg data:', error);
    return [];
  }
};
const STATUS_KEYS_FOR_CHART = ['Successful', 'Failed', 'InProgress', 'Delayed', 'Aborted'];

const getDisplayStatusKey = (status: string): string =>
  status === 'InProgress' ? 'In Progress' : status;

const STATUS_CHART_COLORS: Record<string, string> = {
  Successful: '#01C008',
  Failed: '#FE2626',
  'In Progress': '#ECB914',
  Delayed: '#FF38B0',
  Aborted: '#F57600',
};

export interface TransformedChartTableData {
  categories: string[];
  series: { name: string; data: number[]; color: string }[];
  tableColumns: { name: string; key: string; width?: number | string; minWidth?: number }[];
  tableData: Record<string, any>[];
}

const buildStatsAndTotal = (
  data: Record<string, number>,
  statusKeys: string[]
) => {
  let rowTotal = 0;
  const stats: Record<string, any> = {};

  statusKeys.forEach((status) => {
    const val = data?.[status] ?? 0;
    stats[status] = val === 0 ? '-' : val.toLocaleString();
    rowTotal += val;
  });

  return { stats, rowTotal };
};

const buildTableColumns = (
  firstColumnName: string,
  firstColumnKey: string,
  minWidth: number
) => [
  { name: firstColumnName, key: firstColumnKey, minWidth },

  ...STATUS_KEYS_FOR_CHART.map((s) => ({
    name: getDisplayStatusKey(s),
    key: s,
    minWidth: 150,
  })),

  {
    name: 'Total',
    key: 'Total',
    minWidth: 150,
    formatter: ({ row }: any) => {
      const value = row.Total;
      return value === '-' || value == null
        ? '-'
        : Number(value).toLocaleString();
    },
  },
];

export const transformJobTypeByStatusData = (
  rawData: Record<string, Record<string, number>>,
): TransformedChartTableData => {
  if (!rawData || typeof rawData !== 'object') {
    return { categories: [], series: [], tableColumns: [], tableData: [] };
  }

  const categories = Object.keys(rawData);

  const series = STATUS_KEYS_FOR_CHART.map((status) => ({
    name: getDisplayStatusKey(status),
    data: categories.map((cat) => rawData[cat]?.[status] ?? 0),
    color: STATUS_CHART_COLORS[getDisplayStatusKey(status)] || '#9E9E9E',
  }));

  const tableColumns = buildTableColumns('Job Type', 'jobType', 150);

  const tableData = categories.map((cat) => {
    const { stats, rowTotal } = buildStatsAndTotal(
      rawData[cat],
      STATUS_KEYS_FOR_CHART
    );

    return {
      jobType: cat,
      ...stats,
      Total: rowTotal === 0 ? '-' : rowTotal,
    };
  });

  return { categories, series, tableColumns, tableData };
};

export const transformScheduleByHourData = (
  rawData: Record<string, Record<string, number>>,
): TransformedChartTableData => {
  if (!rawData || typeof rawData !== 'object') {
    return { categories: [], series: [], tableColumns: [], tableData: [] };
  }
  
  const categories = Array.from({ length: 24 }, (_, i) => `${i}-${i + 1}`);

  const series = STATUS_KEYS_FOR_CHART.map((status) => ({
    name: getDisplayStatusKey(status),
    data: categories.map((cat) => rawData[cat]?.[status] ?? 0),
    color: STATUS_CHART_COLORS[getDisplayStatusKey(status)] || '#9E9E9E',
  }));

  const tableColumns = buildTableColumns('Hour', 'hour', 100);

  const tableData = categories.map((cat) => {
    const { stats, rowTotal } = buildStatsAndTotal(
      rawData[cat],
      STATUS_KEYS_FOR_CHART
    );

    return {
      hour: cat,
      ...stats,
      Total: rowTotal === 0 ? '-' : rowTotal,
    };
  });

  return { categories, series, tableColumns, tableData };
};

export const getScheduledForNextOptions = (): GenericOption[] => {
  const cstTimezone = TIMEZONE_MAP.CST || 'America/Chicago';
  const now = moment.tz(cstTimezone);
  const remainingHours = Math.max(0, moment.tz(cstTimezone).endOf('day').diff(now, 'hours'));

  const options: GenericOption[] = [
    {
      label: 'None',
      value: 'none',
    },
    {
      label: 'Rest of the day',
      value: 'rest_of_day',
    },
  ];

  // Add Next N hr options with 2-hour gaps
  for (let hourCount = 2; hourCount <= remainingHours; hourCount += 2) {
    options.push({
      label: `Next ${hourCount} hr`,
      value: String(hourCount),
    });
  }

  return options;
};

export const getDefaultColumns = (activeTile: string, groupByValue?: string) => {
  const isScheduled = activeTile === 'Scheduled';

  const baseColumns = isScheduled
    ? [
        'SCHEDULED DATE',
        'SCHEDULED HOUR',
        'JOB NAME',
        'EXPECTED START TIME',
        "TODAY'S RUN START",
        'SCHEDULE MISSED',
        'SCHEDULE INFO',
        'TIMEZONE',
        'SECTOR',
        'MARKET',
        'GDO',
        'LAYER',
        'DOMAIN',
        'PROGRAM',
        'ORIGIN',
        'DESTINATION',
      ]
    : [
        'DATE',
        'HOUR',
        'JOB TYPE',
        'JOB ID',
        'JOB RUN URL',
        'JOB NAME',
        'STATUS',
        'CUSTOM STATUS',
        'START TIME',
        'END TIME',
        'SOURCE',
        'PROGRAM',
        'SECTOR',
        'RUN TIME MIN',
        'DATA ASSET NAME',
        'GDO',
        'LAYER',
        'PROGRAM JOB NAME',
      ];

  if (!groupByValue || groupByValue === 'none') return baseColumns;

  const columnNameMap: Record<string, string> = {
    'date': 'DATE',
    'hour': 'HOUR',
    'job_type': 'JOB TYPE',
    'run_id': 'JOB RUN URL',
    'job_id': 'JOB ID',
    'job_name': 'JOB NAME',
    'execution_status': 'STATUS',
    'start_time': isScheduled ? 'SCHEDULED TIME' : 'START TIME',
    'end_time': 'END TIME',
    'source_type': 'SOURCE',
    'program_name': 'PROGRAM',
    'sector': 'SECTOR',
    'duration_ms': 'RUN TIME MIN',
    'estimated_end_time': 'EXPECTED END TIME',
    'avg_duration_ms': 'AVG RUN TIME MIN',
    'total_exe_count': 'TOTAL RECORDS',
    'is_recurrence': 'RECURRING',
    'recurrence_per_day': 'RECURRENCES PER DAY',
    'trigger_name': 'TRIGGER NAME',
    'trigger_type': 'TRIGGER TYPE',
    'data_asset_name': 'DATA ASSET NAME',
    'silver_table_name': 'SILVER TABLE NAME',
    'bronze_table_name': 'BRONZE TABLE NAME',
    'subscription_name': 'SUBSCRIPTION NAME',
    'resource_group': 'RESOURCE GROUP',
    'resource_name': 'RESOURCE NAME',
    'schedule_info': 'SCHEDULE INFO',
    'schedule_missed': 'SCHEDULE MISSED',
    'today_run_start': 'TODAY\'S RUN START',
    'last_run_start_time': 'LAST RUN START TIME',
    'timezone': 'TIMEZONE',
    'origin':'ORIGIN',
    'destination':'DESTINATION'
  };

  const groupedColumnDisplayName = columnNameMap[groupByValue] || groupByValue.toUpperCase().replace(/_/g, ' ');
  const remainingColumns = baseColumns.filter(col => col !== groupedColumnDisplayName);
  return [groupedColumnDisplayName, ...remainingColumns];
};

export const normalizeStatus = (status = '') => {
  return String(status)
    .replace(/\s+/g, '')
    .toLowerCase();
};

export const isMiscellaneousSelected = (items: any[] = []) =>
  Array.isArray(items) && items.some((item: any) => {
    if (!item) return false;
    if (typeof item === 'string') {
      return String(item).toLowerCase() === 'miscellaneous';
    }
    return String(item.value || item.label || '').toLowerCase() === 'miscellaneous';
  });

export const toOptions = (values: string[] = []): GenericOption[] =>
  values.map((value) => ({ label: value, value }));

export const getTrend = (difference: number) => {
  if (difference > 0) return 'INCREASED';
  if (difference < 0) return 'DECREASED';
  return 'NO_CHANGE';
};

export const columnFilterPayloadToModel = (payload: any[] = []) =>
  payload.reduce<Record<string, any>>((model, filter) => {
    const hasCondition = Boolean(filter.operator) && !Array.isArray(filter.selOptions);
    const selectedOptions = Array.isArray(filter.selOptions) ? filter.selOptions : [];

    model[filter.id] = {
      values: hasCondition ? (filter.values ?? []) : [],
      selOptions: selectedOptions,
      condition: hasCondition
        ? {
            operator: filter.operator,
            value1: filter.values?.[0] ?? null,
            value2: filter.values?.[1] ?? null,
          }
        : undefined,
    };
    return model;
  }, {});

export const toColumnFiltersPayload = (filterModel: Record<string, any>) =>
  Object.entries(filterModel).map(([id, filter]) => {
    const condition = filter.condition;
    const hasCustomCondition = Boolean(
      condition &&
      (condition.operator || condition.value1 !== undefined || condition.value2 !== undefined),
    );
    const conditionValues = condition
      ? [condition.value1, condition.value2].filter((value) => value !== null && value !== undefined)
      : [];
    const selectedValues = Array.isArray(filter.selOptions) && filter.selOptions.length > 0
      ? filter.selOptions
      : [];
    const payload: Record<string, any> = { id };

    if (hasCustomCondition) {
      payload.operator = condition.operator;
      payload.values = conditionValues;
    }
    if (selectedValues.length > 0) {
      payload.selOptions = selectedValues;
    } else if (!hasCustomCondition && Array.isArray(filter.values)) {
      payload.selOptions = filter.values;
    }
    return payload;
  });


export const CUSTOM_STATUS_OPTIONS = [
  'HUNG',
  'LONG RUNNING',
  'ON TRACK',
];

export const CUSTOM_STATUS_TILES = [
  { key: 'HUNG',          label: 'Hung',           summaryKey: 'hung' },
  { key: 'LONG RUNNING',  label: 'Long Running',   summaryKey: 'longRunning' }
];

export const CUSTOM_STATUS_TILE_SET = new Set([
  'HUNG', 'LONG RUNNING'
]);

export const CUSTOM_STATUS_TOOLTIP_TEXT: Record<string, string> = {
  'HUNG': 'jobs are shown for In Progress jobs by default',
  'LONG RUNNING': 'jobs are shown for In Progress jobs by default',
  'SHORT RUN': 'jobs are shown for Completed jobs only',
};

export const CUSTOM_STATUS_COLORS: Record<string, string> = {
  'HUNG': '#8E1538',
  'LONG RUNNING': '#9C7000',
  'SHORT RUNNING': '#EAAA08',
  'ON TRACK': '#2E90FA'
};

export const STATUS_LABELS = {
  HUNG: 'hung',
  LONG_RUNNING: 'long running',
  SHORT_RUN: 'short run',
  SCHEDULED: 'scheduled'
}

// Shared hooks for job summary and Data Assets
export const getInitialPipelineFilters = (): FilterState => ({
  ...DEFAULT_FILTERS,
  scheduledForNext: [],
});

export const getFilterValues = (options?: Array<{ value: string }>) =>
  (options || []).map((option) => option.value);

export const normalizeColumnFiltersForRequest = (columnFilters: any[]) => {
  if (!Array.isArray(columnFilters)) return columnFilters;

  return columnFilters.map((filter) => {
    if (!filter?.operator || !['duration_ms', 'avg_duration_ms'].includes(filter.id)) {
      return filter;
    }

    const toMilliseconds = (value: any) => {
      if (value === null || value === undefined || value === '') return value;
      const minutes = Number(value);
      return Number.isNaN(minutes) ? value : Math.round(minutes * 60 * 1000);
    };

    return {
      ...filter,
      values: Array.isArray(filter.values)
        ? filter.values.map(toMilliseconds)
        : toMilliseconds(filter.values),
    };
  });
};

export const buildPageFilters = (state: { activeTile?: string; searchText?: string }) => ({
  jobStatus: [state.activeTile || 'All'],
  ...(state.searchText ? { searchText: state.searchText } : {}),
});

export const makePipelineDateRange = (
  rangeType: string | null,
  startDate: string | null,
  endDate: string | null,
  timeZone: string,
) => {
  if (!rangeType) return {};

  const dateRange: Record<string, string> = {
    dateTimeRangeType: rangeType,
    timeZone,
  };
  const zone = TIMEZONE_MAP[timeZone] || TIMEZONE_MAP.CST || 'America/Chicago';

  if (rangeType === 'custom' && startDate && endDate) {
    dateRange.startTime = moment(startDate).tz(zone).format('DD-MM-YYYY HH:mm:ss');
    dateRange.endTime = moment(endDate).tz(zone).format('DD-MM-YYYY HH:mm:ss');
  } else if (rangeType === 'today') {
    dateRange.startTime = moment.tz(zone).startOf('day').format('DD-MM-YYYY HH:mm:ss');
    dateRange.endTime = moment.tz(zone).endOf('day').format('DD-MM-YYYY HH:mm:ss');
  }

  return dateRange;
};

export const normalizeRangeType = (value?: string | null) =>
  String(value || '').trim().toLowerCase();

export const isScheduledTile = (tile: string | undefined | null) =>
  String(tile || '').toLowerCase() === 'scheduled';

export const isMiscellaneousSectorSelected = (sectors: any[] = []) =>
  Array.isArray(sectors) && sectors.some((sector) => {
    if (typeof sector === 'string') return sector.toLowerCase() === 'miscellaneous';
    if (sector && typeof sector === 'object') {
      return String(sector.value || sector.label || '').toLowerCase() === 'miscellaneous';
    }
    return false;
  });

export const scheduledOrder = [
  'date',
  'hour',
  'job_name',
  'last_run_start_time',
  'schedule_missed',
  'schedule_info',
  'timezone',
  'sector',
  'market',
  'gdo',
  'layer',
  'domain',
  'program_name',
  'origin',
  'destination',
];
